import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import styles from "../live-seo-page.module.css";

type LifeWithBracesLocalization = {
	title: string;
	introHeading: string;
	introParagraphs: string[];
	sections: Array<{
		heading: string;
		level: "h3" | "h4";
		hideHeading?: boolean;
		paragraphs?: string[];
		list?: string[];
		columns?: Array<{ title: string; items: string[] }>;
	}>;
};

const lifeWithBracesLocalization = {
	[Locale.EN]: {
		title: "Life With Braces",
		introHeading: "A Big Step Toward a New Smile",
		introParagraphs: [
			"You’ve taken a big step by having braces put on at Miami Orthodontic Specialists.",
			"It’s an exciting time, but it also comes with a bit of a learning curve.",
		],
		sections: [
			{
				heading: "Do Braces Hurt?",
				level: "h3",
				paragraphs: [
					"Braces do not hurt while they’re being placed on your teeth or while the wires are being tied in. In fact, for several hours after the appointment, you may not feel anything.",
					"However, once the teeth start to move, your teeth may start to feel sore. This soreness can last anywhere from 2-5 days, depending on the person. The best way to manage this soreness is to eat soft foods and take an over-the-counter pain reliever, if needed.",
					"Make sure to read the bottle for directions, especially if giving to a child.",
				],
			},
			{
				heading: "Tips for Living With Braces",
				level: "h3",
				paragraphs: [
					"As you get used to your new orthodontic treatment, you may need to make a few adjustments, but it'll become second nature before you know it.",
				],
			},
			{
				heading: "Soreness",
				level: "h4",
				paragraphs: [
					"When you first get your braces, you may notice that your teeth and mouth feel a little tender or sore. This is perfectly normal, and we promise your mouth won't be sore forever. To relieve the pain, we recommend dissolving one teaspoon of salt in eight ounces of lukewarm water.",
					"Swish and gargle this solution in your mouth for just a couple of minutes (do not swallow the saltwater).",
					"If your pain is severe and doesn't go away after rinsing, you can also try taking a pain reliever. It's normal for your lips, cheeks, and tongue to become irritated for one to two weeks as they toughen and become used to the braces.",
					"We would be happy to give you some wax that you can put over the braces to lessen the tenderness. If you need some wax, please let us know & follow our social for other great tips.",
				],
			},
			{
				heading: "How Do I Keep My Braces Clean?",
				level: "h4",
				paragraphs: [
					"When you get your braces at Miami Orthodontics Specialists, we recommend maintaining good oral hygiene. Plaque will stick to the braces and increase the risk of cavities if good hygiene is not maintained.",
					"This can lead to swollen, bleeding gums and decalcification (white spots) on the front of the teeth. The ideal oral hygiene routine is to brush after every meal and floss at least once per day.",
					"Mechanical (power) toothbrushes can be beneficial, especially for children who don’t do as well with manual toothbrushes. Floss threaders and proxy brushes can help clean under the wire and around the gums.",
				],
			},
			{
				heading: "Care & Maintenance",
				level: "h4",
				paragraphs: [
					"Brushing with braces can take some getting used to. Maneuvering around brackets and wires takes a little practice. Be sure to brush thoroughly after every meal or snack, so any food particles are removed before bacteria has a chance to grow.",
					"If you’re not able to brush right away, rinsing your mouth with water will help until you can get to a toothbrush.",
					"Follow these tips for the best results:",
				],
				list: [
					"Use a fluoride toothpaste with a soft, rounded-bristle toothbrush.",
					"Braces wear toothbrushes out quickly, so be sure to replace yours as soon as it begins to show signs of wear.",
					"Brush around every part of your braces, as well as every surface of your teeth.",
					"Look for clean and shiny braces, with the edge of the brackets clearly visible. Fuzzy-or dull-looking metal indicates poor brushing.",
					"Floss every night before you go to bed. Tools like floss threaders and waterpiks are helpful if you’re having difficulty with getting a good floss underneath the wires.",
				],
			},
			{
				heading: "Eating With Braces",
				level: "h3",
				paragraphs: [
					"For your treatment to run on time, you want to avoid breaking your braces and distorting the wires. There are certain foods that increase the risk of breakage and should be avoided during treatment.",
					"Don't worry, you'll be eating popcorn and snacking on potato chips again in no time. However, before you enjoy some of the treats you love, you'll need to avoid any foods that could damage your new braces.",
				],
				columns: [
					{
						title: "Foods You Can Eat",
						items: [
							"Dairy — soft cheese, pudding, milk-based drinks",
							"Breads — soft tortillas, pancakes, muffins without nuts",
							"Grains — pasta, soft cooked rice",
							"Meats/poultry — soft cooked chicken, meatballs, lunch meats",
							"Seafood — tuna, salmon, crab cakes",
							"Vegetables — mashed potatoes, steamed spinach, beans",
							"Fruits — applesauce, bananas, fruit juice",
							"Treats — ice cream without nuts, milkshakes, Jell-O, soft cake",
						],
					},
					{
						title: "Foods to Avoid",
						items: [
							"Chewy foods — bagels, licorice",
							"Crunchy foods — popcorn, chips, ice",
							"Sticky foods — caramel candies, chewing gum",
							"Hard foods — nuts, hard candies",
							"Foods that require biting into — corn on the cob, apples, carrots",
						],
					},
				],
			},
			{
				heading: "Food Modifications",
				level: "h4",
				hideHeading: true,
				paragraphs: [
					"Although there are certain foods that need to be avoided, many of your favorite foods can be modified so that you can still eat them with braces. Here are some suggestions:",
				],
				list: [
					"For chicken wings and ribs, you can take the meat off of the bone",
					"Take the corn off of the cob",
					"Cut apples into small pieces to chew with your back teeth",
					"Cook carrots before eating them",
				],
			},
			{
				heading: "Bad Habits",
				level: "h3",
				paragraphs: [
					"There are also a few oral habits you’ll want to steer clear of to keep your braces functioning perfectly.",
					"Chewing on objects like pens and ice cubes, biting your nails, and smoking can all cause unwanted pressure on your braces.",
					"Do your best to find another way to keep yourself busy while undergoing orthodontic treatment.",
				],
			},
			{
				heading: "Loose Teeth",
				level: "h4",
				paragraphs: [
					"If your teeth begin feeling a little loose, don't worry – it’s normal! Your braces must loosen your teeth first to move them into the right position. Once your teeth have been repositioned, they won’t be loose anymore.",
				],
			},
			{
				heading: "Loose Wires, Bands, & Brackets",
				level: "h4",
				paragraphs: [
					"The wires and bands on your braces may come loose. If this happens, please contact us as soon as possible so that we can check and repair your appliance. If any piece of your appliance comes off, be sure to save it and bring it to the office with you.",
					"You can temporarily fix the loose wire by using the back of a spoon or the eraser end of a pencil to carefully and gently push the wire back into place. If the loose wire is causing irritation to your lips or cheeks, put wax or a wet cotton ball over the broken wire to relieve the pain.",
				],
			},
			{
				heading: "Misplaced Archwire, Bracket, or Tie",
				level: "h4",
				paragraphs: [
					"Once your teeth adjust to treatment, they begin to move. When this happens, the archwire that connects them may also move, poking out a bit near the back of the mouth and irritating your cheeks.",
					"You can often move this wire into a better position by using the eraser end of the pencil or a cotton swab. You can manipulate any misplaces wires or ties back into place by gently using a pair of clean tweezers.",
					"If some of the wires or brackets have shifted, and begun causing irritation to your mouth, you can use orthodontic wax to cover the parts that are poking out.",
					"This will help ease the discomfort, but make sure you get in touch with our office as soon as you can so we can fix the actual problem instead of you only masking the symptoms at home.",
				],
			},
			{
				heading: "Caring for Your Orthodontic Appliance",
				level: "h4",
				paragraphs: [
					"Damaged appliances can increase the length of your treatment process, so be sure to take care of all your appliances. Your teeth and jaw can only move into their correct positions if you consistently wear the rubber bands, headgear, retainer, or other appliances prescribed by your doctor.",
					"For example, Invisalign’s clear aligner system will only work if the aligners are worn the 20-22 hours per day as recommended. Following instructions is the only way to ensure your treatment is effective, and to help you achieve the smile you’ve always wanted.",
				],
			},
			{
				heading: "Tips for Athletes & Musicians",
				level: "h4",
				paragraphs: [
					"You can still play sports like normal during your treatment, but remember to protect your teeth with an orthodontic friendly mouth guard, or to remove your Invisalign aligner during practice or the game.",
					"If you have an accident during your athletic activity, check your appliances and your mouth immediately. If the appliances appear damaged or the teeth loosened, schedule an appointment.",
					"If you play an instrument, you may find it a little challenging to become adjusted to playing with your braces. It’s normal to have some difficulty with proper lip position.",
					"Sores can also develop, but the liberal use of wax and warm saltwater rinses will help your lips and cheeks toughen up more quickly than you’d think.",
				],
			},
		],
	},
	[Locale.ES]: {
		title: "Vida con brackets",
		introHeading: "Un gran paso hacia una nueva sonrisa",
		introParagraphs: [
			"Has dado un gran paso al colocarte brackets en Miami Orthodontic Specialists.",
			"Es un momento emocionante, pero también viene con una pequeña curva de aprendizaje.",
		],
		sections: [
			{
				heading: "¿Duelen los brackets?",
				level: "h3",
				paragraphs: [
					"Los brackets no duelen mientras se colocan en tus dientes ni cuando se ajustan los alambres. De hecho, durante varias horas después de la cita, es posible que no sientas nada.",
					"Sin embargo, cuando los dientes comienzan a moverse, pueden sentirse sensibles. Esta sensibilidad puede durar de 2 a 5 días, según cada persona. La mejor forma de manejarla es comer alimentos blandos y tomar un analgésico de venta libre si lo necesitas.",
					"Asegúrate de leer las instrucciones del envase, especialmente si se lo das a un niño.",
				],
			},
			{
				heading: "Consejos para vivir con brackets",
				level: "h3",
				paragraphs: [
					"A medida que te acostumbras a tu nuevo tratamiento de ortodoncia, puede que necesites hacer algunos ajustes, pero se volverán parte de tu rutina antes de que te des cuenta.",
				],
			},
			{
				heading: "Sensibilidad",
				level: "h4",
				paragraphs: [
					"Cuando recibes tus brackets por primera vez, puedes notar que tus dientes y tu boca se sienten un poco sensibles o adoloridos. Esto es completamente normal, y te prometemos que tu boca no estará así para siempre. Para aliviar el dolor, recomendamos disolver una cucharadita de sal en ocho onzas de agua tibia.",
					"Haz buches y gárgaras con esta solución durante un par de minutos (no tragues el agua con sal).",
					"Si el dolor es intenso y no desaparece después de enjuagarte, también puedes probar con un analgésico. Es normal que los labios, las mejillas y la lengua se irriten durante una o dos semanas mientras se fortalecen y se acostumbran a los brackets.",
					"Con gusto podemos darte cera para colocar sobre los brackets y disminuir la sensibilidad. Si necesitas cera, avísanos y síguenos en redes sociales para otros buenos consejos.",
				],
			},
			{
				heading: "¿Cómo mantengo limpios mis brackets?",
				level: "h4",
				paragraphs: [
					"Cuando recibes tus brackets en Miami Orthodontics Specialists, recomendamos mantener una buena higiene oral. La placa se adhiere a los brackets y aumenta el riesgo de caries si no se mantiene una buena higiene.",
					"Esto puede causar encías inflamadas y sangrantes, además de descalcificación (manchas blancas) en la parte frontal de los dientes. La rutina ideal es cepillarse después de cada comida y usar hilo dental al menos una vez al día.",
					"Los cepillos mecánicos o eléctricos pueden ser beneficiosos, especialmente para niños que no se manejan tan bien con un cepillo manual. Los enhebradores de hilo dental y los cepillos interdentales pueden ayudar a limpiar debajo del alambre y alrededor de las encías.",
				],
			},
			{
				heading: "Cuidado y mantenimiento",
				level: "h4",
				paragraphs: [
					"Cepillarse con brackets puede requerir algo de práctica. Maniobrar alrededor de los brackets y alambres toma tiempo. Cepíllate bien después de cada comida o merienda para retirar cualquier partícula de comida antes de que las bacterias tengan oportunidad de crecer.",
					"Si no puedes cepillarte de inmediato, enjuagarte la boca con agua ayudará hasta que puedas usar un cepillo.",
					"Sigue estos consejos para obtener los mejores resultados:",
				],
				list: [
					"Usa pasta dental con flúor y un cepillo de cerdas suaves y redondeadas.",
					"Los brackets desgastan los cepillos rápidamente, así que reemplázalo tan pronto como empiece a mostrar señales de desgaste.",
					"Cepilla alrededor de cada parte de tus brackets y todas las superficies de tus dientes.",
					"Busca brackets limpios y brillantes, con el borde de los brackets claramente visible. El metal opaco o con aspecto borroso indica un cepillado deficiente.",
					"Usa hilo dental todas las noches antes de dormir. Herramientas como enhebradores y waterpiks son útiles si tienes dificultad para pasar el hilo debajo de los alambres.",
				],
			},
			{
				heading: "Comer con brackets",
				level: "h3",
				paragraphs: [
					"Para que tu tratamiento avance a tiempo, debes evitar romper los brackets y deformar los alambres. Hay ciertos alimentos que aumentan el riesgo de roturas y deben evitarse durante el tratamiento.",
					"No te preocupes, pronto volverás a comer popcorn y papitas. Pero antes de disfrutar algunos de tus antojos favoritos, tendrás que evitar alimentos que puedan dañar tus nuevos brackets.",
				],
				columns: [
					{
						title: "Alimentos que puedes comer",
						items: [
							"Lácteos — queso blando, pudín, bebidas a base de leche",
							"Panes — tortillas suaves, panqueques, muffins sin nueces",
							"Granos — pasta, arroz cocido suave",
							"Carnes/aves — pollo cocido suave, albóndigas, fiambres",
							"Mariscos — atún, salmón, croquetas de cangrejo",
							"Vegetales — puré de papa, espinaca al vapor, frijoles",
							"Frutas — puré de manzana, bananas, jugo de fruta",
							"Dulces — helado sin nueces, batidos, gelatina, pastel suave",
						],
					},
					{
						title: "Alimentos que debes evitar",
						items: [
							"Alimentos masticables — bagels, regaliz",
							"Alimentos crujientes — popcorn, papitas, hielo",
							"Alimentos pegajosos — caramelos, chicle",
							"Alimentos duros — nueces, caramelos duros",
							"Alimentos que requieren morder directamente — mazorca de maíz, manzanas, zanahorias",
						],
					},
				],
			},
			{
				heading: "Modificaciones de alimentos",
				level: "h4",
				hideHeading: true,
				paragraphs: [
					"Aunque hay ciertos alimentos que debes evitar, muchos de tus favoritos pueden modificarse para que todavía puedas comerlos con brackets. Estas son algunas sugerencias:",
				],
				list: [
					"Para alitas de pollo y costillas, retira la carne del hueso",
					"Quita el maíz de la mazorca",
					"Corta las manzanas en trozos pequeños para masticarlas con los dientes posteriores",
					"Cocina las zanahorias antes de comerlas",
				],
			},
			{
				heading: "Malos hábitos",
				level: "h3",
				paragraphs: [
					"También hay algunos hábitos orales que conviene evitar para que tus brackets funcionen perfectamente.",
					"Masticar objetos como bolígrafos y cubos de hielo, morderte las uñas y fumar pueden ejercer presión no deseada sobre tus brackets.",
					"Haz lo posible por encontrar otra forma de mantenerte ocupado durante tu tratamiento de ortodoncia.",
				],
			},
			{
				heading: "Dientes flojos",
				level: "h4",
				paragraphs: [
					"Si tus dientes comienzan a sentirse un poco flojos, no te preocupes: ¡es normal! Tus brackets primero deben aflojar los dientes para moverlos a la posición correcta. Una vez que tus dientes se hayan reposicionado, ya no estarán flojos.",
				],
			},
			{
				heading: "Alambres, bandas y brackets flojos",
				level: "h4",
				paragraphs: [
					"Los alambres y bandas de tus brackets pueden aflojarse. Si esto sucede, comunícate con nosotros lo antes posible para que podamos revisar y reparar tu aparato. Si alguna pieza se desprende, guárdala y tráela a la oficina.",
					"Puedes arreglar temporalmente un alambre flojo usando la parte posterior de una cuchara o el borrador de un lápiz para empujarlo con cuidado y suavidad de nuevo a su lugar. Si el alambre irrita tus labios o mejillas, coloca cera o una bolita de algodón húmeda sobre el alambre para aliviar el dolor.",
				],
			},
			{
				heading: "Arco, bracket o ligadura fuera de lugar",
				level: "h4",
				paragraphs: [
					"Cuando tus dientes se ajustan al tratamiento, comienzan a moverse. Cuando esto ocurre, el arco que los conecta también puede moverse y sobresalir un poco cerca de la parte posterior de la boca, irritando tus mejillas.",
					"A menudo puedes mover este alambre a una mejor posición usando el borrador de un lápiz o un hisopo. Puedes manipular con cuidado los alambres o ligaduras fuera de lugar usando unas pinzas limpias.",
					"Si algunos alambres o brackets se han movido y están causando irritación, puedes usar cera de ortodoncia para cubrir las partes que sobresalen.",
					"Esto ayudará a aliviar la incomodidad, pero asegúrate de comunicarte con nuestra oficina tan pronto como puedas para que podamos corregir el problema real en lugar de solo cubrir los síntomas en casa.",
				],
			},
			{
				heading: "Cuidado de tu aparato de ortodoncia",
				level: "h4",
				paragraphs: [
					"Los aparatos dañados pueden aumentar la duración de tu tratamiento, así que cuida todos tus aparatos. Tus dientes y mandíbula solo pueden moverse a sus posiciones correctas si usas constantemente las bandas elásticas, el arco extraoral, el retenedor u otros aparatos recetados por tu doctora.",
					"Por ejemplo, el sistema de alineadores transparentes Invisalign solo funciona si los alineadores se usan de 20 a 22 horas al día según lo recomendado. Seguir las instrucciones es la única forma de asegurar que tu tratamiento sea efectivo y ayudarte a lograr la sonrisa que siempre has querido.",
				],
			},
			{
				heading: "Consejos para atletas y músicos",
				level: "h4",
				paragraphs: [
					"Puedes practicar deportes normalmente durante tu tratamiento, pero recuerda proteger tus dientes con un protector bucal apto para ortodoncia, o retirar tu alineador Invisalign durante la práctica o el juego.",
					"Si tienes un accidente durante una actividad deportiva, revisa tus aparatos y tu boca de inmediato. Si los aparatos parecen dañados o los dientes están flojos, agenda una cita.",
					"Si tocas un instrumento, puede que al principio sea un poco difícil acostumbrarte a tocar con brackets. Es normal tener cierta dificultad con la posición adecuada de los labios.",
					"También pueden aparecer llagas, pero el uso generoso de cera y los enjuagues con agua tibia con sal ayudarán a que tus labios y mejillas se fortalezcan más rápido de lo que imaginas.",
				],
			},
		],
	},
} satisfies Record<Locale, LifeWithBracesLocalization>;

export function LifeWithBracesPage() {
	const getLocalization = useLocalization(lifeWithBracesLocalization);
	const isEnglish = getLocalization("title") === "Life With Braces";
	const isBoldLead = (text: string) =>
		text === "Follow these tips for the best results:" ||
		text === "Sigue estos consejos para obtener los mejores resultados:";
	const linkedCopy = (text: string) => {
		if (text.includes("Miami Orthodontic Specialists")) {
			const [before, after] = text.split("Miami Orthodontic Specialists");
			return (
				<>
					{before}
					<Link to="/" className={styles.inlineLink}>
						Miami Orthodontic Specialists
					</Link>
					{after}
				</>
			);
		}

		if (isEnglish && text.startsWith("Braces do not hurt")) {
			return (
				<>
					<Link to="/metal-braces" className={styles.inlineLink}>
						Braces
					</Link>{" "}
					do not hurt while they’re being placed on your teeth or while the
					wires are being tied in. In fact, for several hours after the
					appointment, you may not feel anything.
				</>
			);
		}

		if (text.includes("redes sociales")) {
			const [before, after] = text.split("redes sociales");
			return (
				<>
					{before}
					<Link
						to="https://www.facebook.com/miamibraces/"
						className={styles.inlineLink}
					>
						redes sociales
					</Link>
					{after}
				</>
			);
		}

		if (text.includes("social")) {
			const [before, after] = text.split("social");
			return (
				<>
					{before}
					<Link
						to="https://www.facebook.com/miamibraces/"
						className={styles.inlineLink}
					>
						social
					</Link>
					{after}
				</>
			);
		}

		return text;
	};

	return (
		<Flex direction="column">
			<HeroStyle2
				heroImageUrl="/group-of-smiling-teens.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<Box className={styles.lifeIntroBand}>
				<Box className={styles.lifeIntroInner}>
					<Heading as="h2" className={styles.lifeSectionHeading}>
						{getLocalization("introHeading")}
					</Heading>
					{getLocalization("introParagraphs").map((paragraph) => (
						<Text as="p" className={styles.lifeCenteredCopy} key={paragraph}>
							{linkedCopy(paragraph)}
						</Text>
					))}
				</Box>
			</Box>
			<Box className={styles.lifeBand}>
				<Box className={styles.lifeContentInner}>
					{getLocalization("sections").map((section, index) => (
						<section className={styles.lifeSection} key={section.heading}>
							{section.hideHeading ? null : (
								<Heading
									as={section.level}
									className={
										section.level === "h3"
											? styles.lifeSubheading
											: styles.lifeMinorHeading
									}
								>
									{section.heading}
								</Heading>
							)}
							{section.paragraphs?.map((paragraph) => (
								<Text as="p" key={paragraph}>
									{isBoldLead(paragraph) ? (
										<strong>{linkedCopy(paragraph)}</strong>
									) : (
										linkedCopy(paragraph)
									)}
								</Text>
							))}
							{index === 2 ? (
								<OptimizedImage
									alt="miami fl braces"
									className={styles.lifeFeatureImage}
									height={1333}
									src="/miami-fl-braces-001.jpg"
									width={2000}
								/>
							) : null}
							{section.list ? (
								<ul className={styles.lifeList}>
									{section.list.map((item) => (
										<li key={item}>{item}</li>
									))}
								</ul>
							) : null}
							{section.columns ? (
								<Grid
									columns={{ initial: "1", sm: "2" }}
									gap="5"
									className={styles.lifeFoodGrid}
								>
									{section.columns.map((column) => (
										<Box key={column.title}>
											<Text as="p">
												<strong>{column.title}</strong>
											</Text>
											<ul className={styles.lifeList}>
												{column.items.map((item) => (
													<li key={item}>{item}</li>
												))}
											</ul>
										</Box>
									))}
								</Grid>
							) : null}
						</section>
					))}
				</Box>
			</Box>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
